import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Building2, ShieldCheck, ShieldAlert, KeyRound, RefreshCcw, Eraser, Activity, Pencil,
  Plus, Trash2, Upload, FileDown, CheckCircle2, XCircle, HelpCircle, Loader2, AlertTriangle, RotateCcw, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/arca`;

const IVA_CONDITIONS = [
  ['responsable_inscripto', 'Responsable Inscripto'],
  ['monotributo', 'Monotributo'],
  ['exento', 'Exento'],
];
const fmtCuit = (c) => (c && c.length === 11 ? `${c.slice(0, 2)}-${c.slice(2, 10)}-${c.slice(10)}` : c || '—');
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—');
const ENV_LABEL = { homologacion: 'HOMOLOGACIÓN', produccion: 'PRODUCCIÓN' };

const apiError = (e, fallback) => {
  const d = e.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(' · ');
  return fallback;
};

export default function ArcaConfig() {
  const [status, setStatus] = useState(null);
  const [certEnv, setCertEnv] = useState(null);
  const [busy, setBusy] = useState('');
  const [taxDialog, setTaxDialog] = useState(false);
  const [csrDialog, setCsrDialog] = useState(false);
  const [posDialog, setPosDialog] = useState(null);
  const [diag, setDiag] = useState(null);
  const [help, setHelp] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/status`, { withCredentials: true });
      setStatus(r.data);
      setCertEnv((prev) => prev || r.data.mode);
    } catch (e) { toast.error(apiError(e, 'Error al cargar la configuración ARCA')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const cert = status?.certificates?.[certEnv] || {};
  const tp = status?.taxpayer || {};

  const call = async (fn, key) => {
    setBusy(key);
    try { await fn(); } finally { setBusy(''); }
  };

  const setMode = (env) => call(async () => {
    try {
      const r = await axios.put(`${API}/mode`, { environment: env }, { withCredentials: true });
      setStatus(r.data); setCertEnv(env);
      toast.success(`Modo ARCA: ${ENV_LABEL[env]}`);
    } catch (e) { toast.error(apiError(e, 'No se pudo cambiar el modo')); }
  }, 'mode');

  const clearCert = () => call(async () => {
    if (!window.confirm(`¿Borrar el CSR, la clave privada y el certificado de ${ENV_LABEL[certEnv]}?`)) return;
    try {
      const r = await axios.post(`${API}/certificate/clear`, { environment: certEnv }, { withCredentials: true });
      setStatus(r.data);
      toast.success('Estado limpiado');
    } catch (e) { toast.error(apiError(e, 'No se pudo limpiar')); }
  }, 'clear');

  const runDiagnostics = () => call(async () => {
    try {
      const r = await axios.post(`${API}/diagnostics`, { environment: certEnv }, { withCredentials: true });
      setDiag(r.data);
    } catch (e) { toast.error(apiError(e, 'No se pudo ejecutar el diagnóstico')); }
  }, 'diag');

  const uploadCert = (file) => call(async () => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('environment', certEnv);
    try {
      const r = await axios.post(`${API}/certificate/upload`, fd, { withCredentials: true });
      setStatus(r.data.status);
      toast.success(`Certificado cargado · vence ${fmtDate(r.data.info.not_after)}`);
    } catch (e) { toast.error(apiError(e, 'No se pudo cargar el certificado'), { duration: 8000 }); }
  }, 'upload');

  // v3.31.4 · Carga de par externo (.key + .crt) para onboarding de producción cuando el usuario
  // ya tramitó directamente en el portal AFIP sin pasar por el CSR del sistema.
  const [pairDialog, setPairDialog] = useState(false);
  const [pairKey, setPairKey] = useState(null);
  const [pairCert, setPairCert] = useState(null);
  // v3.31.6 · Wizard de renovación anual del certificado (banner + 2 caminos).
  const [renewDialog, setRenewDialog] = useState(false);
  const uploadPair = () => call(async () => {
    if (!pairKey || !pairCert) {
      toast.error('Seleccioná primero ambos archivos (.key y .crt)');
      return;
    }
    const fd = new FormData();
    fd.append('key_file', pairKey);
    fd.append('cert_file', pairCert);
    fd.append('environment', certEnv);
    try {
      const r = await axios.post(`${API}/certificate/upload-pair`, fd, { withCredentials: true });
      setStatus(r.data.status);
      toast.success(`Par cargado · alias ${r.data.info?.subject?.match(/CN\s*=\s*([^,]+)/)?.[1] || '—'} · vence ${fmtDate(r.data.info.not_after)}`);
      setPairDialog(false);
      setPairKey(null);
      setPairCert(null);
    } catch (e) { toast.error(apiError(e, 'No se pudo cargar el par'), { duration: 10000 }); }
  }, 'upload-pair');

  const deletePos = (pos) => call(async () => {
    if (!window.confirm(`¿Eliminar el punto de venta ${pos.numero}?`)) return;
    try {
      const r = await axios.delete(`${API}/points-of-sale/${pos.pos_id}`, { withCredentials: true });
      setStatus(r.data);
      toast.success('Punto de venta eliminado');
    } catch (e) { toast.error(apiError(e, 'No se pudo eliminar'), { duration: 8000 }); }
  }, 'pos');

  if (!status) return <p className="text-muted-foreground py-10 text-center">Cargando…</p>;

  const certOk = cert.state === 'cargado' && !cert.expired;

  return (
    <div className="space-y-6" data-testid="arca-config-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Facturación electrónica ARCA</h1>
          <p className="text-sm text-muted-foreground">
            Contribuyente, certificado digital, puntos de venta y diagnóstico de los web services
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setHelp(true)} data-testid="arca-help-btn">
          <HelpCircle className="w-4 h-4 mr-2" />Cómo tramitarlo
        </Button>
      </div>

      {/* ===== Banner de renovación próxima (v3.31.6) ===== */}
      {certOk && cert.days_to_expire != null && cert.days_to_expire <= 30 && (
        <div
          className={`rounded-md border p-4 flex items-start gap-3 ${
            cert.days_to_expire <= 7
              ? 'border-red-400 bg-red-50 dark:bg-red-950/30'
              : 'border-amber-400 bg-amber-50 dark:bg-amber-950/30'
          }`}
          data-testid="arca-cert-renewal-banner"
        >
          <AlertTriangle className={`w-5 h-5 mt-0.5 ${cert.days_to_expire <= 7 ? 'text-red-600' : 'text-amber-600'}`} />
          <div className="flex-1">
            <p className={`font-semibold ${cert.days_to_expire <= 7 ? 'text-red-700' : 'text-amber-700'}`}>
              Tu certificado de {ENV_LABEL[certEnv]} vence en {cert.days_to_expire} día{cert.days_to_expire === 1 ? '' : 's'}
              {' '}({fmtDate(cert.not_after)})
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Si vence, no vas a poder emitir facturas electrónicas hasta subir uno nuevo. Dejá el circuito listo con unos días de anticipación.
            </p>
          </div>
          <Button size="sm" onClick={() => setRenewDialog(true)} data-testid="arca-renew-cta-banner">
            <RotateCcw className="w-4 h-4 mr-2" />Renovar certificado
          </Button>
        </div>
      )}

      {/* ===== Ficha del contribuyente + certificado + modo ===== */}
      <Card data-testid="arca-taxpayer-card">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 mt-1 text-slate-500" />
              <div>
                <p className="font-bold uppercase tracking-tight" data-testid="arca-business-name">
                  {tp.business_name || 'Sin contribuyente configurado'}
                </p>
                <p className="text-sm text-muted-foreground">{tp.trade_name || '—'}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <code className="rounded bg-muted px-2 py-1 text-xs" data-testid="arca-cuit">{fmtCuit(tp.cuit)}</code>
                  <Badge variant="outline" className="text-xs">
                    {IVA_CONDITIONS.find(([k]) => k === tp.iva_condition)?.[1] || '—'}
                  </Badge>
                  {tp.iibb && <Badge variant="outline" className="text-xs">IIBB {tp.iibb}</Badge>}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setTaxDialog(true)} data-testid="arca-edit-taxpayer">
              <Pencil className="w-4 h-4" />
            </Button>
          </div>

          <div className="border-t mt-5 pt-5 grid gap-6 md:grid-cols-2">
            <div>
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-semibold uppercase text-muted-foreground">
                  Certificado digital · administración
                </p>
                <Tabs value={certEnv} onValueChange={setCertEnv}>
                  <TabsList className="h-7">
                    <TabsTrigger value="homologacion" className="text-[11px] px-2" data-testid="arca-cert-tab-homo">Cert. de Homolog.</TabsTrigger>
                    <TabsTrigger value="produccion" className="text-[11px] px-2" data-testid="arca-cert-tab-prod">Cert. de Producción</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="flex items-center gap-2 mt-3" data-testid="arca-cert-state">
                {certOk
                  ? <><ShieldCheck className="w-4 h-4 text-emerald-600" />
                      <span className="font-semibold text-emerald-700">Certificado cargado</span>
                      <span className="text-xs text-muted-foreground">(Vence: {fmtDate(cert.not_after)} · {cert.days_to_expire} días)</span></>
                  : <><ShieldAlert className="w-4 h-4 text-amber-600" />
                      <span className="font-semibold text-amber-700">
                        {cert.state === 'csr_pendiente' ? 'CSR generado · falta cargar el .crt'
                          : cert.expired ? 'Certificado vencido' : 'Sin certificado'}
                      </span></>}
              </div>
              {cert.alias && (
                <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                  alias {cert.alias}{cert.issuer ? ` · emisor ${cert.issuer.slice(0, 60)}` : ''}
                </p>
              )}
              {cert.wsaa_ok === true && (
                <p className="text-[11px] text-emerald-700 mt-1" data-testid="arca-wsaa-ok">
                  Login WSAA verificado por ARCA el {fmtDate(cert.wsaa_checked_at)}
                </p>
              )}
              {cert.environment_warning && (
                <p className="text-[11px] text-amber-700 mt-1" data-testid="arca-env-warning">
                  {cert.environment_warning}
                </p>
              )}
              {cert.cert_cuit && (
                <p className="text-[11px] text-muted-foreground mt-1" data-testid="arca-cert-cuit">
                  Representante: CUIT {cert.cert_cuit}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-sm">
                <button className="flex items-center gap-1.5 text-slate-700 hover:underline disabled:opacity-40"
                  disabled={!cert.has_key}
                  onClick={() => window.open(`${API}/certificate/key.pem?environment=${certEnv}`, '_blank')}
                  data-testid="arca-download-key">
                  <KeyRound className="w-4 h-4" />Descargar .key
                </button>
                <button className="flex items-center gap-1.5 text-slate-700 hover:underline disabled:opacity-40"
                  disabled={!cert.has_csr}
                  onClick={() => window.open(`${API}/certificate/csr.pem?environment=${certEnv}`, '_blank')}
                  data-testid="arca-download-csr">
                  <FileDown className="w-4 h-4" />Descargar CSR
                </button>
                <button className="flex items-center gap-1.5 text-amber-700 hover:underline"
                  onClick={() => setCsrDialog(true)} data-testid="arca-regen-csr">
                  <RefreshCcw className="w-4 h-4" />{cert.has_csr ? 'Regenerar CSR' : 'Generar CSR'}
                </button>
                <label className="flex items-center gap-1.5 text-slate-700 hover:underline cursor-pointer">
                  <Upload className="w-4 h-4" />{busy === 'upload' ? 'Cargando…' : 'Cargar .crt'}
                  <input type="file" accept=".crt,.pem,.cer" className="hidden" data-testid="arca-upload-crt"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadCert(f); }} />
                </label>
                <button className="flex items-center gap-1.5 text-emerald-700 hover:underline"
                  onClick={() => setPairDialog(true)} data-testid="arca-upload-pair-open">
                  <Upload className="w-4 h-4" />Cargar par (.key + .crt)
                </button>
                <button className="flex items-center gap-1.5 text-slate-500 hover:underline"
                  onClick={clearCert} data-testid="arca-clear-state">
                  <Eraser className="w-4 h-4" />Limpiar estado
                </button>
                <button className="flex items-center gap-1.5 text-indigo-600 hover:underline"
                  onClick={runDiagnostics} disabled={busy === 'diag'} data-testid="arca-diagnostics">
                  {busy === 'diag' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Activity className="w-4 h-4" />}
                  Diagnóstico ARCA
                </button>
                <button className="flex items-center gap-1.5 text-sky-700 hover:underline font-medium"
                  onClick={() => setRenewDialog(true)} data-testid="arca-renew-open">
                  <RotateCcw className="w-4 h-4" />Renovar certificado
                </button>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                Modo de emisión · a qué WSFE le pedís CAE
              </p>
              {status.app_env === 'preview' && (
                <div className="mt-2 rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-3 flex items-start gap-2"
                     data-testid="arca-preview-banner">
                  <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="text-xs">
                    <p className="font-semibold text-amber-800 dark:text-amber-200">
                      Ambiente de TESTING — Emisión en Producción bloqueada
                    </p>
                    <p className="text-amber-700 dark:text-amber-300 mt-1">
                      Este preview tiene prohibido emitir facturas reales. Aunque un backup productivo deje
                      <code className="mx-1 px-1 bg-amber-100 dark:bg-amber-900 rounded">mode=produccion</code>,
                      el sistema fuerza Homologación automáticamente.
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 mt-3">
                <Badge className={status.mode === 'produccion' ? 'bg-emerald-600' : 'bg-amber-500'}
                  data-testid="arca-mode-badge">Modo actual: {status.mode_label}</Badge>
                {status.mode === 'produccion'
                  ? <button className="text-sm text-slate-600 hover:underline" onClick={() => setMode('homologacion')}
                      data-testid="arca-mode-to-homo">Cambiar a modo Homologación</button>
                  : (status.production_mode_locked
                      ? <span className="text-xs text-muted-foreground italic" data-testid="arca-mode-locked">
                          🔒 Cambio a Producción bloqueado en este ambiente
                        </span>
                      : <button className="text-sm text-slate-600 hover:underline" onClick={() => setMode('produccion')}
                          data-testid="arca-mode-to-prod">Cambiar a modo Producción</button>)}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {status.mode === 'homologacion'
                  ? 'Los comprobantes que se autoricen en homologación son de PRUEBA y no tienen validez fiscal. Usá este modo sólo en el entorno de pruebas.'
                  : 'Modo fiscal real: cada comprobante autorizado obtiene un CAE válido ante ARCA.'}
              </p>
              <div className="mt-3 rounded-md border bg-muted/40 p-3 text-xs">
                <span className="uppercase text-muted-foreground">Estado de emisión</span>
                <p className="mt-1 font-semibold" data-testid="arca-can-emit">
                  {status.can_emit
                    ? 'Listo para emitir con CAE'
                    : 'Falta completar la configuración (contribuyente, certificado y al menos un punto de venta activo)'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== Puntos de venta ===== */}
      <Card data-testid="arca-pos-card">
        <CardHeader className="flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base">Puntos de venta</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Cada punto de venta lleva su propia numeración correlativa. Tienen que estar dados de alta
              en ARCA como tipo <b>Web Services</b>.
            </p>
          </div>
          <Button size="sm" onClick={() => setPosDialog({ numero: '', description: '', environment: certEnv, letters: ['A', 'B'], is_default: false, active: true })}
            data-testid="arca-add-pos">
            <Plus className="w-4 h-4 mr-2" />Agregar
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead className="w-28">Número</TableHead>
              <TableHead>Descripción</TableHead>
              <TableHead className="w-36">Ambiente</TableHead>
              <TableHead className="w-32">Talonarios</TableHead>
              <TableHead className="w-28">Estado</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {status.points_of_sale.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6" data-testid="arca-pos-empty">
                  Todavía no hay puntos de venta. Agregá el de homologación para empezar a probar.
                </TableCell></TableRow>
              )}
              {status.points_of_sale.map((p) => (
                <TableRow key={p.pos_id} data-testid={`arca-pos-row-${p.numero}`}>
                  <TableCell className="font-mono font-semibold">{p.numero}</TableCell>
                  <TableCell className="text-sm">{p.description || '—'}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={p.environment === 'produccion' ? 'border-emerald-500 text-emerald-700' : 'border-amber-500 text-amber-700'}>
                      {ENV_LABEL[p.environment]}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{(p.letters || []).join(' · ')}</TableCell>
                  <TableCell className="text-xs">
                    {p.active ? <span className="text-emerald-700">Activo</span> : <span className="text-muted-foreground">Inactivo</span>}
                    {p.is_default && <Badge variant="secondary" className="ml-1 text-[10px]">por defecto</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => setPosDialog(p)} data-testid={`arca-edit-pos-${p.numero}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="text-red-600" onClick={() => deletePos(p)}
                      data-testid={`arca-delete-pos-${p.numero}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <TaxpayerDialog open={taxDialog} onClose={() => setTaxDialog(false)} taxpayer={tp} onSaved={setStatus} />
      <CsrDialog open={csrDialog} onClose={() => setCsrDialog(false)} environment={certEnv}
        taxpayer={tp} cert={cert} onSaved={setStatus} />
      <PosDialog pos={posDialog} onClose={() => setPosDialog(null)} onSaved={setStatus} />
      <DiagnosticsDialog diag={diag} onClose={() => setDiag(null)} />
      <HelpDialog open={help} onClose={() => setHelp(false)} />
      <RenewDialog
        open={renewDialog}
        onClose={() => setRenewDialog(false)}
        environment={certEnv}
        cert={cert}
        onPickReuseKey={() => { setRenewDialog(false); setCsrDialog(true); }}
        onPickExternalPair={() => { setRenewDialog(false); setPairDialog(true); }}
      />
      <Dialog open={pairDialog} onOpenChange={(o) => !o && setPairDialog(false)}>
        <DialogContent className="max-w-lg" data-testid="arca-upload-pair-dialog">
          <DialogHeader>
            <DialogTitle>Cargar par externo (.key + .crt)</DialogTitle>
            <DialogDescription>
              Si tramitaste el certificado directamente en el portal AFIP (sin usar el CSR del sistema),
              podés cargar acá la clave privada y el certificado juntos. Ambiente destino:{' '}
              <b>{ENV_LABEL[certEnv]}</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Clave privada (.key)</Label>
              <input type="file" accept=".key,.pem" className="block mt-1 text-xs"
                data-testid="arca-pair-key-input"
                onChange={(e) => setPairKey(e.target.files?.[0] || null)} />
              {pairKey && <p className="text-[11px] text-muted-foreground mt-1 font-mono">{pairKey.name}</p>}
              <p className="text-[11px] text-muted-foreground mt-1">
                PEM sin password. Si fue exportada con password, re-exportala sin password desde el portal.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase text-muted-foreground">Certificado (.crt)</Label>
              <input type="file" accept=".crt,.pem,.cer" className="block mt-1 text-xs"
                data-testid="arca-pair-cert-input"
                onChange={(e) => setPairCert(e.target.files?.[0] || null)} />
              {pairCert && <p className="text-[11px] text-muted-foreground mt-1 font-mono">{pairCert.name}</p>}
            </div>
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-2 text-[11px] text-amber-900 dark:text-amber-200">
              <b>Importante:</b> el sistema valida que el <code>.crt</code> corresponda a la <code>.key</code>,
              que no esté vencido, y (si el ambiente destino es Producción) que el emisor no sea de la CA de
              Homologación de AFIP. Si cargás un par válido, se descarta cualquier CSR previo del ambiente.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPairDialog(false); setPairKey(null); setPairCert(null); }}
              data-testid="arca-pair-cancel">Cancelar</Button>
            <Button onClick={uploadPair} disabled={!pairKey || !pairCert || busy === 'upload-pair'}
              data-testid="arca-pair-submit">
              {busy === 'upload-pair' ? 'Cargando…' : 'Cargar par'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaxpayerDialog({ open, onClose, taxpayer, onSaved }) {
  const [form, setForm] = useState(taxpayer || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ iva_condition: 'responsable_inscripto', ...taxpayer }); }, [open, taxpayer]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        cuit: (form.cuit || '').trim(),
        business_name: (form.business_name || '').trim(),
        trade_name: (form.trade_name || '').trim() || null,
        iva_condition: form.iva_condition || 'responsable_inscripto',
        address: (form.address || '').trim() || null,
        iibb: (form.iibb || '').trim() || null,
        activity_start: form.activity_start || null,
      };
      const r = await axios.put(`${API}/taxpayer`, body, { withCredentials: true });
      onSaved(r.data); toast.success('Contribuyente guardado'); onClose();
    } catch (e) { toast.error(apiError(e, 'Error al guardar el contribuyente'), { duration: 8000 }); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="arca-taxpayer-dialog">
        <DialogHeader>
          <DialogTitle>Datos del contribuyente</DialogTitle>
          <DialogDescription>Se usan para el CSR y para la cabecera de los comprobantes fiscales.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>CUIT *</Label>
            <Input value={form.cuit || ''} onChange={set('cuit')} placeholder="30716764075" className="font-mono" data-testid="arca-tp-cuit" /></div>
          <div><Label>Condición frente al IVA</Label>
            <select value={form.iva_condition || 'responsable_inscripto'} onChange={set('iva_condition')}
              className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="arca-tp-iva">
              {IVA_CONDITIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></div>
          <div className="sm:col-span-2"><Label>Razón social *</Label>
            <Input value={form.business_name || ''} onChange={set('business_name')} data-testid="arca-tp-name" /></div>
          <div className="sm:col-span-2"><Label>Nombre de fantasía</Label>
            <Input value={form.trade_name || ''} onChange={set('trade_name')} data-testid="arca-tp-trade" /></div>
          <div className="sm:col-span-2"><Label>Domicilio comercial</Label>
            <Input value={form.address || ''} onChange={set('address')} data-testid="arca-tp-address" /></div>
          <div><Label>Ingresos Brutos</Label>
            <Input value={form.iibb || ''} onChange={set('iibb')} data-testid="arca-tp-iibb" /></div>
          <div><Label>Inicio de actividades</Label>
            <Input type="date" value={form.activity_start || ''} onChange={set('activity_start')} data-testid="arca-tp-start" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="arca-tp-save">{saving ? 'Guardando…' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CsrDialog({ open, onClose, environment, taxpayer, cert, onSaved }) {
  const [alias, setAlias] = useState('');
  const [csr, setCsr] = useState('');
  const [saving, setSaving] = useState(false);
  const hasPrevious = !!cert?.has_csr;
  useEffect(() => {
    if (open) { setCsr(''); setAlias(cert?.alias || (environment === 'produccion' ? 'hogar-prod' : 'hogar-homo')); }
  }, [open, environment, cert]);

  const generate = async () => {
    if (hasPrevious && !csr && !window.confirm(
      'Ya hay una solicitud (CSR) generada. Si generás otra, se reemplaza la clave privada y el ' +
      'certificado que ya tramitaste en ARCA deja de servir: tendrías que crearlo de nuevo. ¿Continuar?')) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/certificate/csr`,
        { alias, environment, confirm_replace: true }, { withCredentials: true });
      setCsr(r.data.csr_pem); onSaved(r.data.status);
      toast.success('CSR generado · la clave privada quedó guardada en el servidor');
    } catch (e) { toast.error(apiError(e, 'No se pudo generar el CSR'), { duration: 8000 }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="arca-csr-dialog">
        <DialogHeader>
          <DialogTitle>Generar CSR · {ENV_LABEL[environment]}</DialogTitle>
          <DialogDescription>
            Se genera un par de claves RSA 2048. La <b>clave privada nunca se envía a ARCA</b>: queda
            cifrada en el servidor (podés bajarla como resguardo). Sólo se sube el CSR.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {hasPrevious && !csr && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-xs" data-testid="arca-csr-warning">
              <b>Ojo:</b> ya hay una solicitud generada
              {cert?.state === 'cargado' ? ' y un certificado cargado' : ''}. Generar otra reemplaza la clave
              privada y deja inservible el certificado que hayas tramitado en ARCA. Si estás en medio del
              trámite, cerrá esto y usá <b>Descargar CSR</b>.
            </div>
          )}
          <div className="rounded-md border bg-muted/40 p-3 text-xs">
            <span className="uppercase text-muted-foreground">Subject del CSR</span>
            <p className="font-mono mt-1">C=AR, O={taxpayer.business_name || '—'}, CN={alias || '—'}, serialNumber=CUIT {taxpayer.cuit || '—'}</p>
          </div>
          <div><Label>Alias *</Label>
            <Input value={alias} onChange={(e) => setAlias(e.target.value)} className="font-mono" data-testid="arca-csr-alias" />
            <p className="text-[11px] text-muted-foreground mt-1">
              El mismo alias que vas a usar en ARCA al crear el certificado.
            </p>
          </div>
          {csr && (
            <div>
              <Label>CSR generado</Label>
              <textarea readOnly value={csr} rows={8}
                className="w-full rounded-md border p-2 font-mono text-[10px]" data-testid="arca-csr-text" />
              <div className="flex gap-2 mt-2">
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(csr); toast.success('CSR copiado'); }}
                  data-testid="arca-csr-copy">Copiar</Button>
                <Button variant="outline" size="sm"
                  onClick={() => window.open(`${API}/certificate/csr.pem?environment=${environment}`, '_blank')}
                  data-testid="arca-csr-download">Descargar .csr</Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          <Button onClick={generate} disabled={saving || !alias.trim()} data-testid="arca-csr-generate">
            {saving ? 'Generando…' : csr ? 'Regenerar' : 'Generar CSR'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PosDialog({ pos, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (pos) setForm({ ...pos }); }, [pos]);

  const toggleLetter = (l) => setForm((f) => ({
    ...f, letters: (f.letters || []).includes(l) ? f.letters.filter((x) => x !== l) : [...(f.letters || []), l],
  }));

  const save = async () => {
    setSaving(true);
    try {
      const body = { numero: form.numero, description: form.description, environment: form.environment,
                     letters: form.letters, is_default: !!form.is_default, active: !!form.active };
      const r = form.pos_id
        ? await axios.put(`${API}/points-of-sale/${form.pos_id}`, body, { withCredentials: true })
        : await axios.post(`${API}/points-of-sale`, body, { withCredentials: true });
      onSaved(r.data); toast.success('Punto de venta guardado'); onClose();
    } catch (e) { toast.error(apiError(e, 'Error al guardar'), { duration: 8000 }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!pos} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="arca-pos-dialog">
        <DialogHeader>
          <DialogTitle>{form.pos_id ? `Punto de venta ${form.numero}` : 'Nuevo punto de venta'}</DialogTitle>
          <DialogDescription>La numeración de cada punto de venta es independiente y correlativa.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label>Número *</Label>
            <Input value={form.numero || ''} onChange={(e) => setForm({ ...form, numero: e.target.value })}
              placeholder="00003" className="font-mono" data-testid="arca-pos-numero" /></div>
          <div><Label>Ambiente</Label>
            <select value={form.environment || 'homologacion'} onChange={(e) => setForm({ ...form, environment: e.target.value })}
              className="flex h-10 w-full rounded-md border px-2 text-sm" data-testid="arca-pos-env">
              <option value="homologacion">Homologación (pruebas)</option>
              <option value="produccion">Producción</option>
            </select></div>
          <div className="sm:col-span-2"><Label>Descripción</Label>
            <Input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Facturación residentes" data-testid="arca-pos-desc" /></div>
          <div className="sm:col-span-2">
            <Label>Talonarios habilitados</Label>
            <div className="flex gap-2 mt-1">
              {['A', 'B', 'C', 'M'].map((l) => (
                <button key={l} type="button" onClick={() => toggleLetter(l)}
                  className={`h-9 w-9 rounded-md border font-mono text-sm ${(form.letters || []).includes(l) ? 'bg-slate-900 text-white' : 'bg-background'}`}
                  data-testid={`arca-pos-letter-${l}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="arca-pos-active" />
            <Label className="mb-0">Activo</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={!!form.is_default} onCheckedChange={(v) => setForm({ ...form, is_default: v })} data-testid="arca-pos-default" />
            <Label className="mb-0">Usar por defecto</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving} data-testid="arca-pos-save">{saving ? 'Guardando…' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DiagnosticsDialog({ diag, onClose }) {
  return (
    <Dialog open={!!diag} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="arca-diag-dialog">
        <DialogHeader>
          <DialogTitle>Diagnóstico ARCA · {diag?.environment_label}</DialogTitle>
          <DialogDescription className="font-mono text-[11px] break-all">
            {diag?.wsaa_url}<br />{diag?.wsfe_url}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {(diag?.checks || []).map((c, i) => (
            <div key={i} className="flex items-start gap-3 rounded-md border p-3" data-testid={`arca-diag-check-${i}`}>
              {c.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                    : <XCircle className="w-5 h-5 text-red-600 mt-0.5" />}
              <div>
                <p className="text-sm font-semibold">{c.name}</p>
                <p className="text-xs text-muted-foreground">{c.detail}</p>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="arca-diag-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// v3.31.6 · Wizard de renovación anual: presenta los dos caminos oficiales.
function RenewDialog({ open, onClose, environment, cert, onPickReuseKey, onPickExternalPair }) {
  const hasKey = !!cert?.has_key;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="arca-renew-dialog">
        <DialogHeader>
          <DialogTitle>Renovar certificado · {ENV_LABEL[environment]}</DialogTitle>
          <DialogDescription>
            Elegí cómo vas a tramitar el certificado nuevo en el portal AFIP. Ambas opciones son válidas:
            quedate con la que te resulte más cómoda.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <button
            type="button"
            onClick={onPickReuseKey}
            disabled={!hasKey}
            className="w-full text-left rounded-md border p-4 hover:bg-slate-50 dark:hover:bg-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition"
            data-testid="arca-renew-pick-reuse"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-emerald-100 dark:bg-emerald-950 p-2">
                <KeyRound className="w-5 h-5 text-emerald-700" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">A) Reutilizar la clave privada actual <span className="text-emerald-700 text-xs">(recomendado)</span></p>
                <p className="text-xs text-muted-foreground mt-1">
                  Mantenés el mismo <code>.key</code> ya cargado en el sistema. Vas a:
                </p>
                <ol className="list-decimal ml-5 text-xs text-muted-foreground mt-1 space-y-0.5">
                  <li>Descargar el CSR desde el próximo paso (o reutilizar el existente).</li>
                  <li>Entrar al portal AFIP → <b>Administración de Certificados Digitales</b> → el mismo alias → <b>Agregar certificado</b> → pegar el CSR → descargar el <code>.crt</code>.</li>
                  <li>Volver acá y cargar el <code>.crt</code> con <b>Cargar .crt</b>.</li>
                </ol>
                {!hasKey && (
                  <p className="text-[11px] text-amber-700 mt-2">
                    No hay clave privada guardada en el sistema para este ambiente: usá la opción B.
                  </p>
                )}
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground mt-2" />
            </div>
          </button>

          <button
            type="button"
            onClick={onPickExternalPair}
            className="w-full text-left rounded-md border p-4 hover:bg-slate-50 dark:hover:bg-slate-900 transition"
            data-testid="arca-renew-pick-pair"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-sky-100 dark:bg-sky-950 p-2">
                <Upload className="w-5 h-5 text-sky-700" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">B) Tramitar un par nuevo directo en el portal AFIP</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Generás clave + certificado dentro del portal AFIP (sin usar el CSR del sistema). Vas a:
                </p>
                <ol className="list-decimal ml-5 text-xs text-muted-foreground mt-1 space-y-0.5">
                  <li>En el portal AFIP → <b>Administración de Certificados Digitales</b> → generar un alias nuevo con clave y certificado nuevos.</li>
                  <li>Descargar la <code>.key</code> (sin password) y el <code>.crt</code>.</li>
                  <li>Volver acá y usar <b>Cargar par (.key + .crt)</b> para subir ambos juntos.</li>
                </ol>
                <p className="text-[11px] text-muted-foreground mt-2">
                  El sistema valida que el par sea coherente, que no esté vencido y (si el ambiente es Producción) que
                  no venga de la CA de Homologación.
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground mt-2" />
            </div>
          </button>

          <div className="rounded-md border bg-muted/40 p-3 text-[11px] text-muted-foreground">
            <b>Tip:</b> no borres el certificado anterior hasta que el nuevo haya emitido al menos una factura con CAE.
            Mientras ARCA no rechace, el que está cargado sigue sirviendo.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="arca-renew-cancel">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HelpDialog({ open, onClose }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="arca-help-dialog">
        <DialogHeader>
          <DialogTitle>Cómo tramitar el certificado en ARCA</DialogTitle>
          <DialogDescription>Homologación y Producción son dos trámites y dos certificados distintos.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <p className="font-semibold text-amber-700">1) Homologación (pruebas, sin validez fiscal)</p>
            <ol className="list-decimal ml-5 space-y-1 mt-1 text-muted-foreground">
              <li>Cargá el contribuyente acá y generá el <b>CSR</b> (alias, ej. <code>hogar-homo</code>).</li>
              <li>Entrá a ARCA con <b>Clave Fiscal de una persona física</b> (WSASS no es delegable). Si no ves el servicio, adherí <b>WSASS</b> desde Administrador de Relaciones.</li>
              <li><b>WSASS → Nuevo Certificado</b>: pegá el CSR y guardá el resultado como <code>.crt</code>.</li>
              <li><b>WSASS → Crear Autorización a Servicio</b>: CUIT representado = el de la sociedad, servicio <b>wsfe – Facturación Electrónica</b>.</li>
              <li>Volvé acá, cargá el <code>.crt</code> en la pestaña Homologación y corré <b>Diagnóstico ARCA</b>.</li>
            </ol>
          </div>
          <div>
            <p className="font-semibold text-emerald-700">2) Producción (certificado definitivo)</p>
            <ol className="list-decimal ml-5 space-y-1 mt-1 text-muted-foreground">
              <li>Generá un CSR nuevo en la pestaña <b>Producción</b> (alias distinto, ej. <code>hogar-prod</code>).</li>
              <li>ARCA con <b>Clave Fiscal nivel 3 del representante legal</b> → <b>Administración de Certificados Digitales → Agregar alias</b> → subí el CSR → descargá el <code>.crt</code>.</li>
              <li><b>Administrador de Relaciones → Nueva Relación → AFIP/ARCA → WebServices → Facturación Electrónica</b> y elegí el alias.</li>
              <li>Dá de alta el <b>punto de venta tipo Web Services</b> en <i>Administración de puntos de venta y domicilios</i> (no sirve el del talonario manual ni el de Comprobantes en Línea).</li>
              <li>Cargá el <code>.crt</code> de producción, agregá el punto de venta acá y pasá el modo a <b>PRODUCCIÓN</b>.</li>
            </ol>
          </div>
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs">
            <p className="font-semibold">Importante</p>
            <ul className="list-disc ml-4 mt-1 space-y-1">
              <li>El certificado de homologación <b>no funciona</b> en producción y viceversa: el sistema lo valida por el emisor y rechaza la carga cruzada.</li>
              <li>Las pruebas se hacen en el entorno de <b>Preview</b>; producción sólo recibe el certificado definitivo.</li>
              <li>Guardá el <code>.key</code> y el <code>.crt</code> en un lugar seguro: si el entorno de pruebas se re-clona, se vuelven a cargar sin volver a tramitar nada.</li>
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="arca-help-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
